<template>
  <div>
    <SavedFilters
      :userFilter="userFilter"
      savedFilterId="resource-table"
      @filter-changed="onFilterChanged"
    ></SavedFilters>
    <table class="asd-table">
      <TableHeading :leadingCols="leadingCols"
                    :trailingCols="[]"
                    :cols="resourcePropertiesToShow"
                    :defaultSorter="defaultSorter"
                    :userSorter="userSorter"
                    :addButton="addButton"
                    @sorter-changed="onSorterChanged"
      ></TableHeading>
      <TableFilter :leadingCols="leadingCols"
                   :trailingCols="[]"
                   :cols="resourcePropertiesToShow"
                   :userFilter="userFilter"
                   @filter-changed="onFilterChanged"
      ></TableFilter>
      <tbody>
        <ResourceRow v-for="resource in paged"
                     :key="resource.id"
                     :resourceId="resource.id"
                     :propsToShow="resourcePropertiesToShow"
        ></ResourceRow>
      </tbody>
    </table>
    <Paginator v-model="currentPage"
               :numPages="numPages"
               @input="onPageChanged"
    ></Paginator>
  </div>
</template>

<script>
import TableFilter from 'CORE/components/table-view/filter'
import TableHeading from 'CORE/components/table-view/heading'
import SavedFilters from 'CORE/components/table-view/saved-filters'
import Paginator from 'CORE/components/table-view/paginator'
import ResourceRow from './row'
import _ from 'lodash'
import { czSorter } from 'CORE/utils/sorters'
import { normalizeFts } from 'CORE/components/data-type/comparators'

export default {
    name: 'ResourceTable',
    components: {
        ResourceRow,
        TableFilter,
        TableHeading,
        SavedFilters,
        Paginator,
    },
    props: {
        rootFilter: {
            required: false,
            default: _ => {},
            type: Object,
        },
        queryFilter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        querySorter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        queryPager: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        addButton: {
            type: Object,
            required: false,
            default: _ => null,
        },
        resourceStatus: {
            type: String,
            required: false,
            default: _ => '$active',
        },
    },
    data() {
        return {
            userFilter: {},
            currentPage: 0,
            userSorter: {},
        }
    },
    computed: {
        defaultSorter() {
            return _.get(this.$store.getters.currentUser, 'company.settings.gui.sorter.resources', { column: '_type', order: 'asc' })
        },
        leadingCols() {
            const cols = [null, { label: 'Type', key: '_type' }]
            return cols
        },
        filtered() {
            const filter = _.merge(this.userFilter, this.rootFilter)
            let resources = []
            if (this.resourceStatus === '$active') {
                resources = this.$store.getters.resources
            } else if (this.resourceStatus === '$deleted') {
                resources = this.$store.getters.disabledResources
            } else {
                resources = this.$store.getters.resources
            }
            if (_.size(filter) === 0) {
                return resources
            }
            return _.filter(resources, p => {
                let matches = 0
                _.each(filter, (filterValue, column) => {
                    if (column === '_resourceType') {
                        const projectType = this.$store.getters.projectTypeById(_.get(p, 'resourceType.id'))
                        const normalizedFilterValue = normalizeFts(filterValue)
                        const normalizedProjectName = normalizeFts(_.get(projectType, 'name'))
                        if (normalizedProjectName.search(normalizedFilterValue) >= 0) {
                            matches++
                        }
                    } else {
                        const normalizedFtsValue = normalizeFts(_.get(p, `propertyValues.${column}.$fts`, ''))
                        if (normalizedFtsValue.search(normalizeFts(filterValue)) >= 0) {
                            matches++
                        }
                    }
                })
                return matches === _.size(filter)
            })
        },
        sorted() {
            const defaultColumn = 'system-name'
            let resources = this.filtered
            resources = _.sortWith(resources, (taskA, taskB) => {
                let sortingValueA; let sortingValueB
                if (_.get(this, 'userSorter.column') === '_resourceType') {
                    sortingValueA = _.get(taskA, `project.id`)
                    sortingValueB = _.get(taskB, `project.id`)
                } else if (_.get(this, 'userSorter.column') === '_type') {
                    sortingValueA = _.get(this.$store.getters.projectTypeById(_.get(taskA, `resourceType.id`)), 'name')
                    sortingValueB = _.get(this.$store.getters.projectTypeById(_.get(taskB, `resourceType.id`)), 'name')
                } else {
                    sortingValueA = _.get(taskA, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                    sortingValueB = _.get(taskB, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                }
                const sorter = czSorter(_.get(this, 'userSorter.order', 'desc'))(sortingValueA, sortingValueB)
                return sorter
            })
            return resources
        },
        paged() {
            const paging = _.get(this.$store.getters.settings, 'gui.projectsPaging', 20)
            const start = this.currentPage * paging
            const end = start + paging
            return _.slice(this.sorted, start, end)
        },
        resourcePropertiesToShow() {
            let propertyTypes = []
            _.forEach(_.map(this.filtered, 'propertyValues'), obj => {
                propertyTypes = _.union(propertyTypes, _.keys(obj))
            })
            propertyTypes = _.filter(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                if (_.includes(_.get(pt, 'settings.visible', []), 'resourcesTable')) {
                    return p
                }
            })
            propertyTypes = _.sortBy(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                return _.parseInt(_.get(pt, 'settings.order', 0))
            })
            return _.defaults(propertyTypes, [])
        },
        resourcePropertiesObjectsToShow() {
            const tp = {}
            if (this.resourcePropertiesToShow) {
                for (const p of this.resourcePropertiesToShow) {
                    _.set(tp, p, this.$store.getters.propertyTypeByKey(p))
                }
                return tp
            }
            return {}
        },
        numPages() {
            return Math.ceil(_.size(this.filtered) / _.get(this.$store.getters.settings, 'gui.resourcesPaging', 20))
        },
    },
    mounted() {
        this.userFilter = this.queryFilter
        this.userSorter = this.querySorter
        this.currentPage = _.get(this.queryPager, 'page', 0)
    },
    methods: {
        onFilterChanged(params) {
            this.$set(this, 'userFilter', _.pickBy(params, f => _.size(f) > 0))
            this.$emit('filter-changed', this.userFilter)
        },
        onSorterChanged(params) {
            this.userSorter = params
            this.$emit('sorter-changed', this.userSorter)
        },
        onPageChanged(page) {
            this.$emit('pager-changed', { page, numPages: this.numPages })
        },
    },
}
</script>
